/**
 * Image compression utilities for chat images
 * Compresses images to reduce file size while maintaining reasonable quality
 */

import { logger } from "./logger";

export interface CompressionOptions {
  maxWidth?: number;
  maxHeight?: number;
  quality?: number; // 0-1, default 0.8
  maxFileSizeMB?: number; // Target max file size in MB
  outputMimeType?: "image/jpeg" | "image/png" | "image/webp";
}

const DEFAULT_OPTIONS: Required<Omit<CompressionOptions, "outputMimeType">> = {
  maxWidth: 1920,
  maxHeight: 1920,
  quality: 0.8,
  maxFileSizeMB: 2, // Target 2MB, but we'll try to get as close as possible
};

/**
 * Compresses an image file to reduce its size
 * @param file - The image file to compress
 * @param options - Compression options
 * @returns Promise<File> - The compressed image file
 */
export async function compressImage(
  file: File,
  options: CompressionOptions = {}
): Promise<File> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const outputMimeType =
    options.outputMimeType ??
    (file.type === "image/png" ? "image/png" : "image/jpeg");

  return new Promise((resolve, reject) => {
    let objectUrl: string | null = null;
    const releaseObjectUrl = () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
    };

    try {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      if (!ctx) {
        reject(new Error("Failed to get canvas context"));
        return;
      }

      img.onload = async () => {
        releaseObjectUrl();
        try {
          // Calculate new dimensions while maintaining aspect ratio
          let { width, height } = img;
          const aspectRatio = width / height;

          if (width > opts.maxWidth) {
            width = opts.maxWidth;
            height = width / aspectRatio;
          }

          if (height > opts.maxHeight) {
            height = opts.maxHeight;
            width = height * aspectRatio;
          }

          // Set canvas dimensions
          canvas.width = width;
          canvas.height = height;

          const drawSourceImage = () => {
            // Resizing a canvas resets its drawing state, so apply these on
            // every progressive downscale.
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = "high";
            if (outputMimeType === "image/jpeg") {
              // JPEG has no alpha channel. A white base prevents transparent
              // reference art from becoming black during normalization.
              ctx.fillStyle = "#ffffff";
              ctx.fillRect(0, 0, width, height);
            }
            ctx.drawImage(img, 0, 0, width, height);
          };
          drawSourceImage();

          // Try to compress to target file size
          let quality = opts.quality;
          let compressedBlob: Blob | null = null;
          let attempts = 0;
          const maxAttempts = 5;
          const maxBlobBytes = opts.maxFileSizeMB * 1024 * 1024 * 1.1;

          // Binary search for optimal quality
          let minQuality = 0.3;
          let maxQuality = opts.quality;

          const encodeCanvas = () =>
            new Promise<Blob>((resolveBlob, rejectBlob) => {
              try {
                canvas.toBlob(
                  blob => {
                    if (blob === null) {
                      rejectBlob(new Error("Canvas toBlob returned null"));
                    } else {
                      resolveBlob(blob);
                    }
                  },
                  outputMimeType,
                  quality
                );
              } catch (error) {
                rejectBlob(error);
              }
            });

          while (attempts < maxAttempts) {
            compressedBlob = await encodeCanvas();

            const sizeMB = compressedBlob.size / (1024 * 1024);

            // If we're within 10% of target or below, we're good
            if (sizeMB <= opts.maxFileSizeMB * 1.1) {
              break;
            }

            // If still too large, reduce quality
            if (sizeMB > opts.maxFileSizeMB) {
              maxQuality = quality;
              quality = (minQuality + quality) / 2;
            } else {
              // If smaller than target, try increasing quality slightly
              minQuality = quality;
              quality = Math.min((quality + maxQuality) / 2, opts.quality);
            }

            attempts++;
          }

          // Highly detailed PNGs and screenshots can remain over the byte
          // target even at low JPEG quality. Progressively reducing dimensions
          // makes the advertised 5MB picker limit reliable instead of letting
          // those files fail at the internal data-URL boundary.
          let resizeAttempts = 0;
          while (
            compressedBlob &&
            compressedBlob.size > maxBlobBytes &&
            resizeAttempts < 3 &&
            (width > 640 || height > 640)
          ) {
            width = Math.max(1, Math.round(width * 0.8));
            height = Math.max(1, Math.round(height * 0.8));
            canvas.width = width;
            canvas.height = height;
            drawSourceImage();
            compressedBlob = await encodeCanvas();
            resizeAttempts++;
          }

          if (!compressedBlob) {
            reject(new Error("Failed to compress image"));
            return;
          }

          // Create new File object with compressed data
          const outputExtension =
            outputMimeType === "image/png"
              ? "png"
              : outputMimeType === "image/webp"
                ? "webp"
                : "jpg";
          const outputName = file.name.replace(
            /(?:\.[^.]+)?$/,
            `.${outputExtension}`
          );
          const compressedFile = new File([compressedBlob], outputName, {
            type: outputMimeType,
            lastModified: Date.now(),
          });

          const originalSizeMB = file.size / (1024 * 1024);
          const compressedSizeMB = compressedFile.size / (1024 * 1024);
          const compressionRatio =
            ((file.size - compressedFile.size) / file.size) * 100;

          logger.info("Image compressed", {
            component: "ImageCompression",
            metadata: {
              originalSize: file.size,
              compressedSize: compressedFile.size,
              originalSizeMB: originalSizeMB.toFixed(2),
              compressedSizeMB: compressedSizeMB.toFixed(2),
              compressionRatio: compressionRatio.toFixed(1) + "%",
              quality: quality.toFixed(2),
              dimensions: `${width}x${height}`,
            },
          });

          resolve(compressedFile);
        } catch (error) {
          logger.error(
            "Error during image compression",
            {
              component: "ImageCompression",
              metadata: { fileName: file.name, fileType: file.type },
            },
            error as Error
          );
          reject(error);
        }
      };

      img.onerror = () => {
        releaseObjectUrl();
        logger.error(
          "Error loading image for compression",
          {
            component: "ImageCompression",
            metadata: { fileName: file.name, fileType: file.type },
          },
          new Error("Image failed to load")
        );
        reject(new Error("Failed to load image for compression"));
      };

      // Load the image
      objectUrl = URL.createObjectURL(file);
      img.src = objectUrl;
    } catch (error) {
      releaseObjectUrl();
      logger.error(
        "Error setting up image compression",
        {
          component: "ImageCompression",
          metadata: { fileName: file.name, fileType: file.type },
        },
        error as Error
      );
      reject(error);
    }
  });
}

/**
 * Checks if an image file should be compressed
 * @param file - The file to check
 * @param maxSizeMB - Maximum file size in MB before compression is needed
 * @returns boolean - True if compression is recommended
 */
export function shouldCompressImage(
  file: File,
  maxSizeMB: number = 1
): boolean {
  const sizeMB = file.size / (1024 * 1024);
  return sizeMB > maxSizeMB;
}
